import { ThresholdLevel } from './types';

let guidCounter = 0;

const generateId = (): string => {
    guidCounter += 1;
    return `threshold_${Date.now()}_${guidCounter}`;
};

export const createDefaultThreshold = (
    minScore: number,
    maxScore: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    return [{
        id: generateId(),
        levelIndex: 1,
        minValue: Number.parseFloat(minScore.toFixed(nrOfDecimals)),
        maxValue: Number.parseFloat(maxScore.toFixed(nrOfDecimals)),
        rewardValue: Number.parseFloat(maxScore.toFixed(nrOfDecimals)),
    }];
};

export const sanitizeThresholds = (
    thresholds: ThresholdLevel[],
    maxScore: number,
    minScore: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    if (!thresholds || thresholds.length === 0) {
        return [];
    }

    const sorted = [...thresholds].sort((a, b) => a.levelIndex - b.levelIndex);

    return sorted.map((t, index) => {
        const levelIndex = index + 1;
        const isHighest = index === sorted.length - 1;
        const isLowest = index === 0;

        // Ensure minValue is not below minScore for the lowest level
        let minValue = Number(t.minValue);
        if (isLowest) {
            minValue = Math.max(minValue, minScore);
        }

        // Ensure maxValue does not exceed maxScore
        let maxValue = Number(t.maxValue);
        if (isHighest) {
            maxValue = maxScore;
        }
        maxValue = Math.min(maxValue, maxScore);

        // Ensure minValue <= maxValue (the >= rule)
        if (minValue > maxValue) {
            minValue = maxValue;
        }

        return {
            ...t,
            levelIndex,
            minValue: Number.parseFloat(minValue.toFixed(nrOfDecimals)),
            maxValue: Number.parseFloat(maxValue.toFixed(nrOfDecimals)),
        };
    });
};

export const canAddThreshold = (
    thresholds: ThresholdLevel[], maxScore: number, step: number = 1
): boolean => {
    // AC2.1/AC2.2: Total threshold count must not exceed n - 1 (where n = maxScore/default marks)
    if (maxScore > 0 && thresholds.length >= maxScore - 1) {
        return false;
    }
    // Cannot add thresholds if maxScore is too small to support more levels
    if (maxScore <= 1) {
        return false;
    }
    if (thresholds.length === 0) {
        return true;
    }
    // Check if cascade would produce a valid state
    const sorted = [...thresholds].sort((a, b) => a.levelIndex - b.levelIndex);
    const lowestLevel = sorted[0];
    if (Number(lowestLevel.minValue) + step <= Number(lowestLevel.maxValue)) {
        return true;
    }
    // Simulate cascade: each threshold shifts up by step unless it can absorb
    for (const t of sorted) {
        const newMin = Number(t.minValue) + step;
        if (Number(t.maxValue) >= newMin + step) {
            return true; // This threshold can absorb the cascade
        }
        const newMax = Math.min(Number(t.maxValue) + step, maxScore);
        if (newMin > newMax) {
            return false; // Cascade would produce invalid state (minValue > maxValue)
        }
    }
    return true;
};

export const addThreshold = (
    thresholds: ThresholdLevel[],
    maxScore: number,
    step: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    if (!canAddThreshold(thresholds, maxScore, step)) {
        return thresholds;
    }

    if (thresholds.length === 0) {
        return createDefaultThreshold(0, maxScore, nrOfDecimals);
    }

    const sorted = [...thresholds].sort((a, b) => a.levelIndex - b.levelIndex);
    const lowestLevel = sorted[0];

    // AC2.5: Add new threshold (0,0) at the bottom.
    // The previously lowest threshold's minValue increases by step to make room.
    // AC2.6: If the lowest threshold already has To=From (can't shrink), cascade upward.
    let updatedThresholds = sorted.map((t) => ({ ...t, levelIndex: t.levelIndex + 1 }));

    if (Number(lowestLevel.minValue) + step <= Number(lowestLevel.maxValue)) {
        // AC2.5: Simple case — just increase the previously lowest level's minValue
        updatedThresholds = updatedThresholds.map((t) => {
            if (t.levelIndex === 2) { // was levelIndex 1 before bump
                return { ...t, minValue: Number.parseFloat((Number(lowestLevel.minValue) + step).toFixed(nrOfDecimals)) };
            }
            return t;
        });
    } else {
        // AC2.6: Cascade — each threshold increases by step until we find one with range > 0
        let cascaded = false;
        let cascadeInvalid = false;
        updatedThresholds = updatedThresholds.map((t) => {
            if (cascaded || cascadeInvalid) {
                return t;
            }
            const newMin = Number.parseFloat((Number(t.minValue) + step).toFixed(nrOfDecimals));
            if (Number(t.maxValue) >= newMin + step) {
                // This threshold has enough room — absorb the cascade
                cascaded = true;
                return { ...t, minValue: newMin };
            }
            // This threshold is already minimal (To=From), shift both up
            // IB-19731: Cap maxValue at maxScore to prevent range exceeding maximum mark
            const newMax = Math.min(
                Number.parseFloat((Number(t.maxValue) + step).toFixed(nrOfDecimals)),
                maxScore,
            );
            if (newMin > newMax) {
                cascadeInvalid = true;
                return t;
            }
            return {
                ...t,
                minValue: newMin,
                maxValue: newMax,
            };
        });

        if (cascadeInvalid) {
            return thresholds;
        }
    }

    const newLevel: ThresholdLevel = {
        id: generateId(),
        levelIndex: 1,
        minValue: Number.parseFloat(Number(lowestLevel.minValue).toFixed(nrOfDecimals)),
        maxValue: Number.parseFloat(Number(lowestLevel.minValue).toFixed(nrOfDecimals)),
        rewardValue: 0,
    };

    return [newLevel, ...updatedThresholds];
};

export const removeThreshold = (
    thresholds: ThresholdLevel[],
    levelToRemove: ThresholdLevel,
    maxScore: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    const filtered = thresholds.filter((t) => t.id !== levelToRemove.id);
    return filtered.map((t, index) => {
        const newLevelIndex = index + 1;
        let newMaxValue = t.maxValue;

        if (t.levelIndex === levelToRemove.levelIndex - 1) {
            newMaxValue = levelToRemove.maxValue;
        }
        if (newLevelIndex === filtered.length) {
            newMaxValue = maxScore;
        }

        return {
            ...t,
            levelIndex: newLevelIndex,
            maxValue: Number.parseFloat(Number(newMaxValue).toFixed(nrOfDecimals)),
        };
    }).sort((a, b) => a.levelIndex - b.levelIndex);
};

export const updateMinValue = (
    thresholds: ThresholdLevel[],
    levelIndex: number,
    newValue: number,
    step: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    return thresholds.map((t) => {
        if (t.levelIndex === levelIndex) {
            return { ...t, minValue: Number.parseFloat(newValue.toFixed(nrOfDecimals)) };
        }
        if (t.levelIndex === levelIndex - 1) {
            const newMaxValue = newValue - step;
            return { ...t, maxValue: Number.parseFloat(newMaxValue.toFixed(nrOfDecimals)) };
        }
        return { ...t };
    });
};

export const updateRewardValue = (
    thresholds: ThresholdLevel[],
    levelId: string,
    newValue: number,
    nrOfDecimals: number,
): ThresholdLevel[] => {
    return thresholds.map((t) => {
        if (t.id === levelId) {
            return { ...t, rewardValue: Number.parseFloat(Math.max(0, newValue).toFixed(nrOfDecimals)) };
        }
        return { ...t };
    });
};

export const getIntervalMinValue = (
    thresholds: ThresholdLevel[],
    levelIndex: number,
    step: number,
): number => {
    if (levelIndex <= 1) {
        const level = thresholds.find((t) => t.levelIndex === 1);
        return level ? level.minValue : 0;
    }
    const previousLevel = thresholds.find((t) => t.levelIndex === levelIndex - 1);
    if (!previousLevel) {
        return 0;
    }
    return previousLevel.minValue + step;
};
